<modal-dialog #inviteUsersModal [dialogTemplate]="inviteUsersDialogTemplate"></modal-dialog>
<ng-template #inviteUsersDialogTemplate>
  <div
    class="light-content-panel light-control-host margin-auto"
    [class.max-width-500]="!rbacEnabled"
    (keydown.escape)="close()"
    tabindex="0"
    arpSurfaceErrorHost="invite-users-modal"
    [errorHostActions]="['recheck-verification']"
    #dialogError="surfaceErrorHost"
  >
    <div class="modal-header">
      <h2 class="panel-title margin-left">Invite Users</h2>
      <button type="button" class="modal-close-btn" (click)="close()">
        <svg lucideX></svg>
      </button>
    </div>
    <form [formGroup]="inviteForm" (ngSubmit)="onSendInvites()" (keydown.enter)="$event.preventDefault()">
      <div class="modal-body">
        <!-- Email List Section -->
        <div class="form-section">
          <div class="gap-8" [class.flex-row]="rbacEnabled">
            <div class="form-row flex-1">
              <div class="form-group">
                <label class="form-label">Email Addresses</label>
                <div class="email-input-with-button">
                  <div class="email-tags-input-container">
                    <div class="email-tags-wrapper">
                      @for (email of emailTags; track email; let i = $index) {
                        <span class="email-tag" [class.invalid]="isEmailInvalid(email)">
                          {{ email }}
                          <svg lucideX class="tag-remove" (click)="removeEmailTag(i)"></svg>
                        </span>
                      }
                      <input
                        type="text"
                        #emailInput
                        [value]="currentEmailInput"
                        (input)="onEmailInputChange($event)"
                        (keydown)="onEmailInputKeydown($event)"
                        (blur)="onEmailInputBlur()"
                        class="email-input"
                        placeholder="Type email addresses..."
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    class="add-email-button"
                    (click)="addEmailTagFromButton()"
                    [disabled]="!currentEmailInput.trim()"
                    title="Add email"
                  >
                    <svg lucidePlus></svg>
                  </button>
                </div>
              </div>
              @if (emailInputTouched) {
                <arp-field-issue [message]="emailListErrors"></arp-field-issue>
              }
            </div>
            <div class="form-row flex-1">
              <!-- Identity Provider and External ID Section -->
              <div class="form-group">
                <label class="form-label">Identity Provider</label>
                <div class="select-wrapper full-width">
                  <select class="arpio-select full-width" formControlName="identityProvider">
                    @if (ssoIdentityProviders.length > 1) {
                      <option value="">Select an identity provider</option>
                    }
                    @for (idp of ssoIdentityProviders; track idp) {
                      <option [value]="idp.identityProviderId">
                        {{ uiService.buildIdentityProviderName(idp) }}
                      </option>
                    }
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        @if (rbacEnabled) {
          <hr />
        }
        <!-- Create Assignments Section - Hidden when rbacEnabled is false -->
        @if (rbacEnabled) {
          <div
            class="flex-row full-width create-user-assignments-section"
            [ngStyle]="{ gap: accountService.rbacFullEnabled ? '2rem' : '0' }"
          >
            <!-- Initial Roles Table -->
            <div
              class="table-container"
              [ngStyle]="{ flex: accountService.rbacFullEnabled ? '1' : 'auto' }"
              style="width: 100%"
            >
              <div class="flex-row items-center gap-4 half-margin-bottom">
                <h4 class="whitespace-nowrap">Initial Roles</h4>
                <div class="filter-container">
                  @if (isModalOpen()) {
                    <input
                      pInputText
                      type="text"
                      #rolesFilter
                      (input)="filterRoles($event)"
                      placeholder="Filter roles..."
                      class="arpio-input filter-input"
                    />
                  }
                </div>
              </div>
              @if (isModalOpen()) {
                <p-table
                  #rolesTable
                  [value]="availableRoles"
                  [globalFilterFields]="['name', 'accessLevel']"
                  dataKey="id"
                  [scrollable]="true"
                  scrollHeight="300px"
                  [tableStyle]="{ 'min-width': '20rem' }"
                  class="p-datatable-sm"
                  [loading]="loadingRoles"
                  [customSort]="true"
                  (sortFunction)="customSort($event)"
                >
                  <ng-template #header>
                    <tr>
                      <th class="checkbox-column">
                        <input
                          type="checkbox"
                          class="arpio-checkbox"
                          [checked]="areAllRolesSelected()"
                          [indeterminate]="areSomeRolesSelected()"
                          (change)="toggleAllRoles($event)"
                        />
                      </th>
                      <th pSortableColumn="name" class="role-name-column">
                        Role Name <p-sort-icon field="name"></p-sort-icon>
                      </th>
                      <th pSortableColumn="accessLevel">
                        Access Level(s) <p-sort-icon field="accessLevel"></p-sort-icon>
                      </th>
                    </tr>
                  </ng-template>
                  <ng-template #body let-role>
                    <tr [ngClass]="{ 'default-role-row': isDefaultAccountRole(role) }">
                      <td class="checkbox-column">
                        <input
                          type="checkbox"
                          class="arpio-checkbox"
                          [checked]="isRoleSelected(role)"
                          (change)="toggleRole(role, $event)"
                        />
                      </td>
                      <td class="role-name-column">
                        <div>
                          {{ role.name }}
                        </div>
                      </td>
                      <td>
                        @if (role.accessLevels && role.accessLevels.length > 0) {
                          <div class="access-level-badges">
                            @for (accessLevelInfo of role.accessLevels; track accessLevelInfo) {
                              <p-tag
                                [severity]="
                                  accessLevelInfo.accessLevel.includes('admin')
                                    ? 'danger'
                                    : accessLevelInfo.accessLevel.includes('read')
                                      ? 'warning'
                                      : 'info'
                                "
                                class="access-level-badge fit-width"
                                [pTooltip]="getAccessLevelTooltip(accessLevelInfo)"
                                tabindex="0"
                                role="note"
                                [attr.aria-label]="getAccessLevelTooltip(accessLevelInfo)"
                                tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                                tooltipPosition="top"
                                [autoHide]="false"
                                [hideDelay]="200"
                                [escape]="false"
                                tooltipEvent="hover"
                                value="{{ accessLevelService.getAccessLevelDisplayName(accessLevelInfo.accessLevel) }}"
                              >
                              </p-tag>
                            }
                          </div>
                        } @else {
                          <div>{{ accessLevelService.getAccessLevelDisplayName(role.accessLevel) }}</div>
                        }
                      </td>
                    </tr>
                  </ng-template>
                  <ng-template #emptymessage>
                    <tr>
                      <td colspan="3" class="empty-state-cell text-center">
                        <div class="empty-state-content">
                          <p class="empty-state-message">No roles available.</p>
                          <p class="empty-state-submessage">Create roles in Role Management to assign them to users.</p>
                        </div>
                      </td>
                    </tr>
                  </ng-template>
                  <ng-template #summary>
                    <div class="flex align-items-center justify-content-between">
                      <span
                        >Displaying {{ rolesTable.filteredValue?.length ?? rolesTable.value?.length }} of
                        {{ rolesTable.value?.length }} roles</span
                      >
                    </div>
                  </ng-template>
                </p-table>
              }
            </div>
            <!-- Initial Groups Table -->
            @if (accountService.rbacFullEnabled) {
              <div class="table-container" style="flex: 1">
                <div class="flex-row items-center gap-4 half-margin-bottom">
                  <h4 class="whitespace-nowrap">Initial Groups</h4>
                  <div class="filter-container">
                    @if (isModalOpen()) {
                      <input
                        pInputText
                        type="text"
                        #groupsFilter
                        (input)="filterGroups($event)"
                        placeholder="Filter groups..."
                        class="arpio-input filter-input"
                      />
                    }
                  </div>
                </div>
                @if (isModalOpen()) {
                  <p-table
                    #groupsTable
                    [value]="availableGroups"
                    [globalFilterFields]="['name', 'roleCount']"
                    dataKey="id"
                    [scrollable]="true"
                    scrollHeight="300px"
                    [tableStyle]="{ 'min-width': '20rem' }"
                    class="p-datatable-sm"
                  >
                    <ng-template #header>
                      <tr>
                        <th class="checkbox-column">
                          <input
                            type="checkbox"
                            class="arpio-checkbox"
                            [checked]="areAllGroupsSelected()"
                            [indeterminate]="areSomeGroupsSelected()"
                            (change)="toggleAllGroups($event)"
                          />
                        </th>
                        <th pSortableColumn="name">Group Name <p-sort-icon field="name"></p-sort-icon></th>
                      </tr>
                    </ng-template>
                    <ng-template #body let-group>
                      <tr>
                        <td class="checkbox-column">
                          <input
                            type="checkbox"
                            class="arpio-checkbox"
                            [checked]="isGroupSelected(group)"
                            (change)="toggleGroup(group, $event)"
                          />
                        </td>
                        <td>
                          <div>{{ group.name }}</div>
                        </td>
                      </tr>
                    </ng-template>
                    <ng-template #emptymessage>
                      <tr>
                        <td colspan="2" class="empty-state-cell text-center">
                          <div class="empty-state-content">
                            <p class="empty-state-message">No groups available.</p>
                            <p class="empty-state-submessage">
                              Create groups in Group Management to assign them to users.
                            </p>
                          </div>
                        </td>
                      </tr>
                    </ng-template>
                    <ng-template #summary>
                      <div class="flex align-items-center justify-content-between">
                        <span
                          >Displaying {{ groupsTable.filteredValue?.length ?? groupsTable.value?.length }} of
                          {{ groupsTable.value?.length }} groups</span
                        >
                      </div>
                    </ng-template>
                  </p-table>
                }
              </div>
            }
          </div>
        }
      </div>

      @if (dialogError.error) {
        <arp-error-block
          class="dialog-error"
          [error]="dialogError.error"
          [dismissible]="true"
          (actionInvoked)="onErrorAction($event, dialogError)"
          (dismissed)="dialogError.clear()"
        ></arp-error-block>
      }

      <div class="modal-footer">
        @if (dialogError.error?.blockedReason) {
          <span class="modal-footer-hint">{{ dialogError.error.blockedReason }}</span>
        }
        <button type="button" class="arpio-secondary-button" (click)="close()">Cancel</button>
        <button
          type="submit"
          class="arpio-button"
          [disabled]="!isFormValid || sendingInvites || !!dialogError.error?.blockedReason"
          data-cy="send-invites-button"
        >
          @if (sendingInvites) {
            <svg lucideLoaderCircle class="lucide-spin"></svg>
          }
          {{ sendingInvites ? 'Sending...' : 'Send Invites' }}
        </button>
      </div>
    </form>
  </div>
</ng-template>
